import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Form, Button, Table, Alert, Nav, Tab } from 'react-bootstrap';
import axios from 'axios';
import { useNotification, NotificationMessages } from '../hooks/useNotification';
import { useAuth } from '../context/AuthContext';

// Helper function to extract names from various data formats
const extractNames = (names) => {
  if (!names) return '-';
  
  // Helper to remove task from name (format: "Name - Task")
  const removeTask = (name) => {
    if (typeof name === 'string' && name.includes(' - ')) {
      return name.split(' - ')[0].trim();
    }
    return name;
  };
  
  if (typeof names === 'string') {
    if (names.startsWith('[') && names.endsWith(']')) {
      try {
        const parsed = JSON.parse(names);
        if (Array.isArray(parsed)) {
          return parsed.map(removeTask).join(', ');
        }
      } catch (e) {
        return removeTask(names);
      }
    }
    return removeTask(names);
  }
  
  if (Array.isArray(names)) {
    return names.map(removeTask).join(', ');
  }
  
  if (typeof names === 'object') {
    const keys = Object.keys(names);
    if (keys.length > 0) {
      return keys.map(removeTask).join(', ');
    }
  }
  
  return '-';
};

// Helper function to format project staff (used as coordinators in MOOE)
const formatProjectStaff = (projectStaffNames) => {
  if (!projectStaffNames) return '-';
  
  let namesArray = [];
  
  // Parse project_staff_names
  if (typeof projectStaffNames === 'string') {
    if (projectStaffNames.startsWith('[') && projectStaffNames.endsWith(']')) {
      try {
        namesArray = JSON.parse(projectStaffNames);
      } catch (e) {
        namesArray = [projectStaffNames];
      }
    } else {
      namesArray = [projectStaffNames];
    }
  } else if (Array.isArray(projectStaffNames)) {
    namesArray = projectStaffNames;
  } else {
    return '-';
  }
  
  if (namesArray.length === 0) return '-';
  
  // Extract only names (remove any task information if present in format "Name - Task")
  return namesArray.map((name) => {
    // If name includes task (format: "Name - Task"), extract only the name part
    if (typeof name === 'string' && name.includes(' - ')) {
      return name.split(' - ')[0].trim();
    }
    return name;
  }).join(', ');
};

// Format ISO date string to YYYY-MM-DD format
const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

const LineItemBudgetModal = ({ show, onHide, projectId, projectData: initialProjectData, onSuccess }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('mooe');
  const [mooeAmounts, setMooeAmounts] = useState({});
  const [equipmentAmounts, setEquipmentAmounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [existingBudget, setExistingBudget] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [coordinatorDepartment, setCoordinatorDepartment] = useState(null);
  const [budgetStatus, setBudgetStatus] = useState('Pending');
  const [campusColleges, setCampusColleges] = useState(['']);
  const [signatoryPrefs, setSignatoryPrefs] = useState({
    prepared_by_enabled: true,
    checked_by_enabled: true,
    certified_by_enabled: true,
    ra_1_enabled: true,
    ra_2_enabled: true,
    approved_by_enabled: true
  });
  const { notification, showSuccess, showError, dismiss } = useNotification();

  const toggleSignatoryPref = (key) => {
    setSignatoryPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // MOOE items schema
  const mooeItems = useMemo(() => ([
    { key: 'travel_local', label: 'Travelling Cost - Local' },
    { key: 'travel_foreign', label: 'Travelling Cost - Foreign' },
    { key: 'training', label: 'Training Expenses' },
    { key: 'office_supplies', label: 'Supplies and Materials - Office Supplies' },
    { key: 'accountable_forms', label: 'Supplies and Materials - Accountable Forms' },
    { key: 'drugs_medicines', label: 'Supplies and Materials - Drugs and Medicines' },
    { key: 'laboratory_exp', label: 'Supplies and Materials - Laboratory Expenses' },
    { key: 'textbooks', label: 'Supplies and Materials - Textbook and Instructional Materials' },
    { key: 'supplies_others', label: 'Supplies and Materials - Others' },
    { key: 'postage', label: 'Postage and Deliveries' },
    { key: 'tel_mobile', label: 'Communication Expenses - Telephone (Mobile)' },
    { key: 'internet', label: 'Communication Expenses - Internet' },
    { key: 'comm_others', label: 'Communication Expenses - Others' },
    { key: 'rent', label: 'Rent Expenses' },
    { key: 'transport_delivery', label: 'Transportation and Delivery Expenses' },
    { key: 'subscription', label: 'Subscription Expenses' },
    { key: 'consultancy', label: 'Professional Services - Consultancy Services' },
    { key: 'general_services', label: 'Professional Services - General Services' },
    { key: 'other_professional', label: 'Professional Services - Other Professional Services' },
    { key: 'repair_it', label: 'Repairs and Maintenance of Facilities - IT Equipment and Software' },
    { key: 'repair_lab', label: 'Repairs and Maintenance of Facilities - Laboratory Equipment' },
    { key: 'repair_tech', label: 'Repairs and Maintenance - Technical and Scientific Equipment' },
    { key: 'repair_machine', label: 'Repairs and Maintenance - Machineries and Equipment' },
    { key: 'repair_others', label: 'Repairs and Maintenance - Others' },
    { key: 'taxes', label: 'Taxes, Duties, Patent, and Licenses' },
    { key: 'other_mooe', label: 'Other Maintenance and Operating Expenses' },
    { key: 'representation', label: 'Representation Expenses' },
    { key: 'advertising', label: 'Advertising Expenses' },
    { key: 'printing_binding', label: 'Printing and Binding Expenses' },
    { key: 'others_financial_charges', label: 'Others Financial Charges (Contingency = 10% of the Total Project Cost)' },
  ]), []);

  // Equipment items schema
  const equipmentItems = useMemo(() => ([
    { key: 'equip_it', label: 'IT Equipment and Software' },
    { key: 'equip_machineries', label: 'Machineries' },
    { key: 'equip_comm', label: 'Communication Equipment' },
    { key: 'equip_lab', label: 'Laboratory Equipment' },
    { key: 'equip_tech_sci', label: 'Technical and Scientific Equipment' },
    { key: 'equip_others', label: 'Others' },
  ]), []);

  // Initialize amounts
  useEffect(() => {
    const mooeBase = {};
    mooeItems.forEach(item => { mooeBase[item.key] = ''; });
    setMooeAmounts(mooeBase);

    const equipmentBase = {};
    equipmentItems.forEach(item => { equipmentBase[item.key] = ''; });
    setEquipmentAmounts(equipmentBase);
  }, [mooeItems, equipmentItems]);

  // Reset projectData when modal is closed
  useEffect(() => {
    if (!show) {
      setProjectData(null);
    }
  }, [show]);

  // Fetch project data and existing budget data
  useEffect(() => {
    if (show && projectId) {
      const fetchData = async () => {
        try {
          setLoading(true);
          
          // Always fetch fresh project data from API to ensure we have latest coordinator data
          // This is important because coordinator data might be saved after initial project creation
          try {
            const projectResponse = await axios.get(`/projects/${projectId}`);
            const fetchedData = projectResponse.data;
            setProjectData(fetchedData);
            
            console.log('🔍 DEBUG - Fetched project data for MOOE:', {
              project_staff_names: fetchedData.project_staff_names,
              project_staff_tasks: fetchedData.project_staff_tasks,
              project_staff_ids: fetchedData.project_staff_ids,
              coordinator_names: fetchedData.coordinator_names,
              coordinator_tasks: fetchedData.coordinator_tasks,
              coordinator_ids: fetchedData.coordinator_ids
            });
            
            // Get coordinator department
            if (fetchedData.coordinator_id) {
              try {
                const allUsersResponse = await axios.get('/users');
                const coordinator = allUsersResponse.data.find(user => 
                  user.user_id === fetchedData.coordinator_id
                );
                if (coordinator?.department_college) {
                  setCoordinatorDepartment(coordinator.department_college);
                }
              } catch (error) {
                console.error('Error fetching coordinator department:', error);
              }
            }
          } catch (error) {
            console.error('Error fetching project data:', error);
            // Fallback to initialProjectData if fetch fails
            if (initialProjectData) {
              setProjectData(initialProjectData);
            }
          }
          
          // Get coordinator department from fetched project data
          if (projectData?.coordinator_id) {
            try {
              const allUsersResponse = await axios.get('/users');
              const coordinator = allUsersResponse.data.find(user => 
                user.user_id === projectData.coordinator_id
              );
              if (coordinator?.department_college) {
                setCoordinatorDepartment(coordinator.department_college);
              }
            } catch (error) {
              console.error('Error fetching coordinator department:', error);
            }
          }
          
          // Fetch existing budget data
          const response = await axios.get(`/budgets/by-project/${projectId}`);
          if (response.data) {
            setExistingBudget(response.data);
            if (response.data.status) {
              setBudgetStatus(response.data.status);
            }
            setSignatoryPrefs({
              prepared_by_enabled: response.data.prepared_by_enabled !== undefined ? !!response.data.prepared_by_enabled : true,
              checked_by_enabled: response.data.checked_by_enabled !== undefined ? !!response.data.checked_by_enabled : true,
              certified_by_enabled: response.data.certified_by_enabled !== undefined ? !!response.data.certified_by_enabled : true,
              ra_1_enabled: response.data.ra_1_enabled !== undefined ? !!response.data.ra_1_enabled : true,
              ra_2_enabled: response.data.ra_2_enabled !== undefined ? !!response.data.ra_2_enabled : true,
              approved_by_enabled: response.data.approved_by_enabled !== undefined ? !!response.data.approved_by_enabled : true
            });
            try {
              const existingItems = JSON.parse(response.data.items_json || '{}');
              
              // Load MOOE amounts
              const mooeUpdated = {};
              mooeItems.forEach(item => {
                if (existingItems[item.key] !== undefined && existingItems[item.key] !== null) {
                  mooeUpdated[item.key] = existingItems[item.key].toString();
                } else {
                  mooeUpdated[item.key] = '';
                }
              });
              setMooeAmounts(mooeUpdated);
              
              // Load Equipment amounts
              const equipmentUpdated = {};
              equipmentItems.forEach(item => {
                if (existingItems[item.key] !== undefined && existingItems[item.key] !== null) {
                  equipmentUpdated[item.key] = existingItems[item.key].toString();
                } else {
                  equipmentUpdated[item.key] = '';
                }
              });
              setEquipmentAmounts(equipmentUpdated);
              
              // Load campus/college entries
              if (response.data.campus_colleges) {
                try {
                  const parsed = JSON.parse(response.data.campus_colleges);
                  if (Array.isArray(parsed) && parsed.length > 0) {
                    setCampusColleges(parsed);
                  } else {
                    const defaultCampus = coordinatorDepartment || projectData?.coordinator_department || projectData?.department_college || '';
                    setCampusColleges(defaultCampus ? [defaultCampus] : ['']);
                  }
                } catch (e) {
                  const defaultCampus = coordinatorDepartment || projectData?.coordinator_department || projectData?.department_college || '';
                  setCampusColleges(defaultCampus ? [defaultCampus] : ['']);
                }
              } else {
                const defaultCampus = coordinatorDepartment || projectData?.coordinator_department || projectData?.department_college || '';
                setCampusColleges(defaultCampus ? [defaultCampus] : ['']);
              }
            } catch (parseError) {
              console.error('Error parsing existing budget data:', parseError);
            }
          } else {
            const defaultCampus = coordinatorDepartment || projectData?.coordinator_department || projectData?.department_college || '';
            setCampusColleges(defaultCampus ? [defaultCampus] : ['']);
          }
        } catch (error) {
          console.log('No existing budget found');
          const defaultCampus = coordinatorDepartment || projectData?.coordinator_department || projectData?.department_college || '';
          setCampusColleges(defaultCampus ? [defaultCampus] : ['']);
          setBudgetStatus('Pending');
          setSignatoryPrefs({
            prepared_by_enabled: true,
            checked_by_enabled: true,
            certified_by_enabled: true,
            ra_1_enabled: true,
            ra_2_enabled: true,
            approved_by_enabled: true
          });
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [show, projectId, mooeItems, equipmentItems, initialProjectData]);

  const parseNumber = (v) => {
    const num = parseFloat(v);
    return isNaN(num) ? 0 : num;
  };

  const mooeSubtotal = useMemo(() => 
    mooeItems.reduce((sum, i) => sum + parseNumber(mooeAmounts[i.key]), 0), 
    [mooeAmounts, mooeItems]
  );

  const equipmentSubtotal = useMemo(() => 
    equipmentItems.reduce((sum, i) => sum + parseNumber(equipmentAmounts[i.key]), 0), 
    [equipmentAmounts, equipmentItems]
  );

  const grandTotal = useMemo(() => mooeSubtotal + equipmentSubtotal, [mooeSubtotal, equipmentSubtotal]);

  const currency = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleMooeAmountChange = (key, value) => {
    if (value === '') {
      setMooeAmounts(prev => ({ ...prev, [key]: '' }));
      return;
    }
    const cleaned = value.replace(/[^0-9.]/g, '');
    setMooeAmounts(prev => ({ ...prev, [key]: cleaned }));
  };

  const handleEquipmentAmountChange = (key, value) => {
    if (value === '') {
      setEquipmentAmounts(prev => ({ ...prev, [key]: '' }));
      return;
    }
    const cleaned = value.replace(/[^0-9.]/g, '');
    setEquipmentAmounts(prev => ({ ...prev, [key]: cleaned }));
  };

  const handleNextTab = async () => {
    if (activeTab === 'mooe') {
      // Save MOOE data when moving to Equipment tab
      await handleSaveMOOE();
      setActiveTab('equipment');
    }
  };

  const handlePreviousTab = () => {
    if (activeTab === 'equipment') {
      setActiveTab('mooe');
    }
  };

  const handleSaveMOOE = async () => {
    if (!projectId) return;
    
    try {
      // Get existing budget or create new one
      let budgetData = existingBudget;
      if (!budgetData) {
        try {
          const response = await axios.get(`/budgets/by-project/${projectId}`);
          budgetData = response.data;
          setExistingBudget(budgetData);
        } catch (error) {
          // Budget doesn't exist yet
        }
      }

      // Get all existing amounts
      let allAmounts = {};
      if (budgetData) {
        try {
          allAmounts = JSON.parse(budgetData.items_json || '{}');
        } catch (e) {
          allAmounts = {};
        }
      }

      // Merge MOOE amounts with existing amounts
      const updatedAmounts = { ...allAmounts, ...mooeAmounts };

      // Calculate totals (use existing equipment amounts)
      const equipmentSubtotalCalc = equipmentItems.reduce((sum, item) => 
        sum + parseNumber(allAmounts[item.key] || equipmentAmounts[item.key] || 0), 0
      );
      const grandTotalCalc = mooeSubtotal + equipmentSubtotalCalc;

      // Filter out empty campus/college entries
      const filteredCampusColleges = campusColleges.filter(c => c && c.trim() !== '');
      
      const budgetPayload = {
        proposal: {
          project_id: projectId,
          title: projectData?.title || 'Untitled Project'
        },
        items: updatedAmounts,
        totals: {
          mooeSubtotal,
          equipmentSubtotal: equipmentSubtotalCalc,
          contingency: 0,
          grandTotal: grandTotalCalc
        },
        campus_colleges: filteredCampusColleges.length > 0 ? filteredCampusColleges : null,
        signatory_prefs: signatoryPrefs,
        // Include status if Admin is editing
        ...(user?.role === 'Admin' && budgetStatus ? { status: budgetStatus } : {})
      };

      if (budgetData) {
        await axios.put(`/budgets/${budgetData.budget_id}`, budgetPayload);
      } else {
        const response = await axios.post('/budgets', budgetPayload);
        setExistingBudget(response.data);
      }
    } catch (err) {
      console.error('Error saving MOOE:', err);
      // Don't show error, just log it - user can retry
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      
      // Get existing budget or create new one
      let budgetData = existingBudget;
      if (!budgetData && projectId) {
        try {
          const response = await axios.get(`/budgets/by-project/${projectId}`);
          budgetData = response.data;
        } catch (error) {
          // Budget doesn't exist yet, will be created
        }
      }

      // Get all existing amounts
      let allAmounts = {};
      if (budgetData) {
        try {
          allAmounts = JSON.parse(budgetData.items_json || '{}');
        } catch (e) {
          allAmounts = {};
        }
      }

      // Merge both MOOE and Equipment amounts
      const updatedAmounts = { ...allAmounts, ...mooeAmounts, ...equipmentAmounts };

      // Calculate totals
      const grandTotalCalc = mooeSubtotal + equipmentSubtotal;

      // Filter out empty campus/college entries
      const filteredCampusColleges = campusColleges.filter(c => c && c.trim() !== '');
      
      const budgetPayload = {
        proposal: {
          project_id: projectId,
          title: projectData?.title || 'Untitled Project'
        },
        items: updatedAmounts,
        totals: {
          mooeSubtotal,
          equipmentSubtotal,
          contingency: 0,
          grandTotal: grandTotalCalc
        },
        campus_colleges: filteredCampusColleges.length > 0 ? filteredCampusColleges : null,
        signatory_prefs: signatoryPrefs,
        // Include status if Admin is editing
        ...(user?.role === 'Admin' && budgetStatus ? { status: budgetStatus } : {})
      };

      // Log payload for debugging (especially status)
      if (user?.role === 'Admin' && budgetStatus) {
        console.log('🔍 Updating budget with status:', {
          budgetId: budgetData?.budget_id,
          status: budgetStatus,
          userRole: user.role
        });
      }

      if (budgetData) {
        await axios.put(`/budgets/${budgetData.budget_id}`, budgetPayload);
      } else {
        await axios.post('/budgets', budgetPayload);
      }

      // Show success modal
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Budget submit failed:', err.response?.data || err);
      const msg = err.response?.data?.details || err.response?.data?.error || 
        err.message || 'Failed to submit Line-Item Budget. Please try again.';
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    dismiss();
    setActiveTab('mooe');
    setBudgetStatus('Pending');
    setSignatoryPrefs({
      prepared_by_enabled: true,
      checked_by_enabled: true,
      certified_by_enabled: true,
      ra_1_enabled: true,
      ra_2_enabled: true,
      approved_by_enabled: true
    });
    onHide();
  };

  const handleAddCampusCollege = () => {
    setCampusColleges([...campusColleges, '']);
  };

  const handleRemoveCampusCollege = (index) => {
    if (campusColleges.length > 1) {
      const updated = campusColleges.filter((_, i) => i !== index);
      setCampusColleges(updated);
    }
  };

  const handleCampusCollegeChange = (index, value) => {
    const updated = [...campusColleges];
    updated[index] = value;
    setCampusColleges(updated);
  };

  const renderProjectInfo = () => (
    <div className="mb-3 p-3 bg-light rounded">
      {/* Status field - Admin only, shown only when editing (existingBudget exists) */}
      {user?.role === 'Admin' && existingBudget && (
        <Form.Group className="mb-3">
          <Form.Label><strong>Budget Status:</strong></Form.Label>
          <Form.Select
            value={budgetStatus}
            onChange={(e) => setBudgetStatus(e.target.value)}
          >
            <option value="Pending">Pending</option>
            <option value="Approved by Extension Head">Approved by Extension Head</option>
            <option value="Rejected by Extension Head">Rejected by Extension Head</option>
            <option value="Approved by Accounting Office">Approved by Accounting Office</option>
            <option value="Rejected by Accounting Office">Rejected by Accounting Office</option>
            <option value="Approved by VC-RDES">Approved by VC-RDES</option>
            <option value="Rejected by VC-RDES">Rejected by VC-RDES</option>
            <option value="Approved by VC-AF">Approved by VC-AF</option>
            <option value="Rejected by VC-AF">Rejected by VC-AF</option>
            <option value="Approved by Chancellor">Approved by Chancellor</option>
            <option value="Rejected by Chancellor">Rejected by Chancellor</option>
          </Form.Select>
          <Form.Text className="text-muted">
            Only Admin can change budget status
          </Form.Text>
        </Form.Group>
      )}
      
      <h6 className="mb-3"><strong>Project Information</strong></h6>
      <div className="mb-2"><strong>Title:</strong> {projectData?.title || '-'}</div>
      <div className="mb-2"><strong>Project Leader:</strong> {(() => {
        const leaderName = projectData?.project_leader_name || '';
        // Remove task if present (format: "Name - Task" or "Honorific Name - Task")
        return leaderName.includes(' - ') ? leaderName.split(' - ')[0].trim() : leaderName || '-';
      })()}</div>
      <div className="mb-2"><strong>Assistant Project Leaders:</strong> {projectData?.assistant_project_leader_names ? extractNames(projectData.assistant_project_leader_names) : '-'}</div>
      <div className="mb-2"><strong>Coordinators:</strong> {formatProjectStaff(projectData?.project_staff_names)}</div>
      
      {/* Campus/College Customization */}
      <Form.Group className="mb-3">
        <Form.Label><strong>Campus/College:</strong></Form.Label>
        {campusColleges.map((campus, index) => (
          <div key={index} className="d-flex mb-2">
            <Form.Control
              type="text"
              value={campus}
              onChange={(e) => handleCampusCollegeChange(index, e.target.value)}
              placeholder="Enter campus/college name"
              className="me-2"
            />
            {campusColleges.length > 1 && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleRemoveCampusCollege(index)}
                title="Remove"
              >
                <i className="bi bi-x-lg"></i>
              </Button>
            )}
          </div>
        ))}
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleAddCampusCollege}
          className="mt-1"
        >
          <i className="bi bi-plus-lg me-1"></i>
          Add Campus/College
        </Button>
        <Form.Text className="text-muted d-block">
          Add multiple campus/college entries as needed (free text)
        </Form.Text>
      </Form.Group>
      
      <div className="mb-2"><strong>Duration:</strong> {(() => {
        if (projectData?.start_date && projectData?.end_date) {
          const startFormatted = formatDate(projectData.start_date);
          const endFormatted = formatDate(projectData.end_date);
          return `${startFormatted} to ${endFormatted}`;
        }
        return '-';
      })()}</div>
    </div>
  );

  const renderMOOETab = () => (
    <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '1rem' }}>
      {renderProjectInfo()}
      
      <div className="table-responsive mb-3">
        <Table bordered hover>
          <thead>
            <tr className="table-secondary">
              <th style={{ width: '70%' }}>ITEM</th>
              <th style={{ width: '30%' }}>AMOUNT (Php)</th>
            </tr>
          </thead>
          <tbody>
            <tr className="table-light">
              <td colSpan={2} className="fw-bold">I. Maintenance and Other Operating Expenses (MOOE)</td>
            </tr>
            {mooeItems.map(item => (
              <tr key={item.key}>
                <td>{item.label}</td>
                <td>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={mooeAmounts[item.key] || ''}
                    onChange={(e) => handleMooeAmountChange(item.key, e.target.value)}
                    style={{ border: '1px solid #ced4da', padding: '8px' }}
                  />
                </td>
              </tr>
            ))}
            <tr className="table-primary">
              <td className="text-end fw-bold">MOOE SUBTOTAL</td>
              <td className="fw-bold">{currency(mooeSubtotal)}</td>
            </tr>
          </tbody>
        </Table>
      </div>
    </div>
  );

  const renderEquipmentTab = () => (
    <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '1rem' }}>
      {renderProjectInfo()}
      
      <div className="table-responsive mb-3">
        <Table bordered hover>
          <thead>
            <tr className="table-secondary">
              <th style={{ width: '70%' }}>ITEM</th>
              <th style={{ width: '30%' }}>AMOUNT (Php)</th>
            </tr>
          </thead>
          <tbody>
            <tr className="table-light">
              <td colSpan={2} className="fw-bold">II. Equipment Outlay</td>
            </tr>
            {equipmentItems.map(item => (
              <tr key={item.key}>
                <td>{item.label}</td>
                <td>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={equipmentAmounts[item.key] || ''}
                    onChange={(e) => handleEquipmentAmountChange(item.key, e.target.value)}
                    style={{ border: '1px solid #ced4da', padding: '8px' }}
                  />
                </td>
              </tr>
            ))}
            <tr className="table-primary">
              <td className="text-end fw-bold">EQUIPMENT OUTLAY SUBTOTAL</td>
              <td className="fw-bold">{currency(equipmentSubtotal)}</td>
            </tr>
            <tr className="table-success">
              <td className="text-end fw-bold">TOTAL PROGRAM/PROJECT COST</td>
              <td className="fw-bold">{currency(grandTotal)}</td>
            </tr>
          </tbody>
        </Table>
      </div>
    </div>
  );

  return (
    <>
      <Modal 
        show={show} 
        onHide={handleClose} 
        size="xl" 
        centered 
        scrollable 
        backdrop="static" 
        keyboard={false}
        style={{ zIndex: 10200 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-calculator me-2"></i>
            Extension Line-Item Budget
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, pointerEvents: 'auto', position: 'relative' }}>
          {notification.show && (
            <Alert variant={notification.variant} dismissible onClose={dismiss} className="m-3 mb-0">
              {notification.message}
            </Alert>
          )}
          
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">Loading budget data...</div>
            </div>
          ) : projectData ? (
            <Tab.Container activeKey={activeTab} onSelect={async (k) => {
              // If switching from MOOE to Equipment, save MOOE data first
              if (activeTab === 'mooe' && k === 'equipment') {
                await handleSaveMOOE();
              }
              setActiveTab(k);
            }}>
              <Nav variant="tabs" className="px-3 pt-3">
                <Nav.Item>
                  <Nav.Link eventKey="mooe">
                    <i className="bi bi-cash-stack me-1"></i>
                    I. MOOE
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="equipment">
                    <i className="bi bi-tools me-1"></i>
                    II. Equipment Outlay
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="signatories">
                    <i className="bi bi-pen me-1"></i>
                    Signatories
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%' }}>
                <Tab.Pane eventKey="mooe" style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%' }}>
                  {renderMOOETab()}
                </Tab.Pane>
                <Tab.Pane eventKey="equipment" style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%' }}>
                  {renderEquipmentTab()}
                </Tab.Pane>
                <Tab.Pane eventKey="signatories" style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%' }}>
                  <div className="p-4">
                    <Alert variant="info" className="mb-3">
                      <i className="bi bi-info-circle me-2"></i>
                      Enable/disable which signatory sections will appear in the generated DOCX. Disabling a signatory will output no
                      name/designation/date/signature for that section.
                    </Alert>

                    {[
                      { key: 'prepared_by_enabled', id: 'lib-signatory-prepared-by', label: 'Prepared by (Project Leader)' },
                      { key: 'checked_by_enabled', id: 'lib-signatory-checked-by', label: 'Checked and Verified (Extension Head)' },
                      { key: 'certified_by_enabled', id: 'lib-signatory-certified-by', label: 'Certified Funds Available (Accounting Office)' },
                      { key: 'ra_1_enabled', id: 'lib-signatory-ra1', label: 'Recommending Approval 1 (VC-RDES)' },
                      { key: 'ra_2_enabled', id: 'lib-signatory-ra2', label: 'Recommending Approval 2 (VC-AF)' },
                      { key: 'approved_by_enabled', id: 'lib-signatory-approved-by', label: 'Approved by (Chancellor)' }
                    ].map((opt) => {
                      const disabled = loading || submitting;
                      return (
                        <div
                          key={opt.key}
                          role="checkbox"
                          aria-checked={!!signatoryPrefs[opt.key]}
                          tabIndex={0}
                          className="d-flex align-items-center gap-2 mb-3"
                          style={{
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            userSelect: 'none',
                            opacity: disabled ? 0.6 : 1
                          }}
                          onClick={() => {
                            if (disabled) return;
                            toggleSignatoryPref(opt.key);
                          }}
                          onKeyDown={(e) => {
                            if (disabled) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleSignatoryPref(opt.key);
                            }
                          }}
                        >
                          <input
                            id={opt.id}
                            type="checkbox"
                            checked={!!signatoryPrefs[opt.key]}
                            readOnly
                            disabled={disabled}
                            style={{
                              pointerEvents: 'none', // ensure ONE click path (container) toggles once
                              marginTop: 2
                            }}
                          />
                          <label
                            htmlFor={opt.id}
                            className="mb-0"
                            style={{ cursor: disabled ? 'not-allowed' : 'pointer', marginLeft: 4 }}
                          >
                            {opt.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '2rem' }}></i>
              <div className="mt-2 text-muted">No project data available</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          {activeTab === 'mooe' && (
            <Button 
              variant="primary" 
              onClick={handleNextTab}
              disabled={loading || !projectData}
            >
              Next →
            </Button>
          )}
          {activeTab === 'equipment' && (
            <>
              <Button 
                variant="outline-primary" 
                onClick={handlePreviousTab}
                disabled={loading || !projectData}
              >
                ← Previous
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSubmit}
                disabled={submitting || loading || !projectData}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
            </>
          )}
          {activeTab === 'signatories' && (
            <>
              <Button
                variant="outline-primary"
                onClick={() => setActiveTab('equipment')}
                disabled={loading || !projectData}
              >
                ← Previous
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={submitting || loading || !projectData}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => {
        setShowSuccessModal(false);
        if (onSuccess) onSuccess();
        handleClose();
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-check-circle-fill text-success me-2"></i>
            Success
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 mb-2">Line-Item Budget Form Submitted Successfully</h5>
            <p className="text-muted">The Line-Item Budget has been saved successfully.</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => {
            setShowSuccessModal(false);
            if (onSuccess) onSuccess();
            handleClose();
          }}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LineItemBudgetModal;

